import { useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { ArrowLeft, Pencil, GitBranch, Send, Loader2, Package2, Building, FileText, AlertTriangle } from "lucide-react";
import { toast } from "sonner";
import api, { apiError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { formatDate, formatINR, formatINRShort } from "@/lib/constants";
import { StatusBadge, DivisionTag } from "@/components/StatusBadge";
import { ActivityTimeline } from "@/components/ActivityTimeline";
import { LeadFormDialog } from "@/components/LeadFormDialog";
import { SampleRequestDialog } from "@/components/SampleRequestDialog";
import { VoiceNoteButton } from "@/components/VoiceNoteButton";
import { EventsCard } from "@/components/EventsCard";
import { RemindersCard } from "@/components/RemindersCard";
import { LeadStatusSelect } from "@/components/LeadStatusSelect";
import { AssignOwnerSelect } from "@/components/AssignOwnerSelect";
import { AssignDealerButton } from "@/components/AssignDealerButton";
import { StageInfo, InfoHint } from "@/components/StageInfo";
import { CompletenessBadge, CompletenessCard } from "@/components/CompletenessBadge";
import { CardImage } from "@/components/CardImage";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { UniverseLinkBanner } from "@/components/UniverseLinkBanner";
import { CallHistoryCard } from "@/components/CallHistoryCard";
import { EmailStatusBadge } from "@/components/EmailStatusBadge";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { Card } from "@/components/ui/card";

function Detail({ label, value }) {
  if (value === undefined || value === null || value === "") return null;
  return (
    <div>
      <p className="text-[11px] font-semibold uppercase tracking-wider text-slate-400">{label}</p>
      <p className="text-sm text-slate-800 mt-0.5">{value}</p>
    </div>
  );
}

function LargeDealCard({ d, value, edc }) {
  const F = [
    ["Sl", d.sl_no], ["Zone", d.zone], ["Branch", d.branch], ["Sales Member", d.sales_member],
    ["Dealer Involved", d.dealer_involved], ["Is UniVic Specified", d.univic_specified],
    ["Identified", d.identified], ["Arch / Designer", d.architect], ["DnB / GC", d.dnb_gc],
    ["End Client Details", d.end_client], ["Competition", d.competition],
    ["Type of Product", d.product_type], ["Total Area (Sqft, lacs)", d.area_lacs_sqft],
    ["Value (INR Cr)", d.value_rs_lacs != null ? (Number(d.value_rs_lacs) / 100).toFixed(2) : null], ["EDC", d.edc],
  ];
  return (
    <Card className="p-5 border-amber-200 bg-amber-50/40" data-testid="large-deal-card">
      <h3 className="font-heading font-semibold text-sm text-slate-800 mb-1 flex items-center gap-2"><Building className="h-4 w-4 text-amber-600" /> Large Project Details</h3>
      <p className="text-xs text-slate-500 mb-4">From the Pan-India Large Projects dashboard (&gt; 1 lakh sqft).{value ? ` Expected value ${formatINRShort(value)}.` : ""}{edc ? ` Expected close ${formatDate(edc)}.` : ""}</p>
      <div className="grid grid-cols-2 sm:grid-cols-3 gap-4">
        {F.filter(([, v]) => v).map(([l, v]) => <Detail key={l} label={l} value={v} />)}
      </div>
    </Card>
  );
}

export default function LeadDetail() {
  const { id } = useParams();
  const navigate = useNavigate();
  const qc = useQueryClient();
  const { user } = useAuth();
  const [editOpen, setEditOpen] = useState(false);
  const [sampleOpen, setSampleOpen] = useState(false);
  const [note, setNote] = useState("");
  const [converting, setConverting] = useState(false);
  const [convertOpen, setConvertOpen] = useState(false);
  const [dealValue, setDealValue] = useState("");

  const { data: lead, isLoading } = useQuery({
    queryKey: ["lead", id],
    queryFn: async () => (await api.get(`/leads/${id}`)).data,
  });

  const addNote = async () => {
    if (!note.trim()) return;
    await api.post(`/leads/${id}/notes`, { description: note, action_type: "note" });
    setNote("");
    qc.invalidateQueries({ queryKey: ["lead", id] });
    toast.success("Note added");
  };

  const convert = async () => {
    if (!Number(dealValue)) { toast.error("Enter the deal value"); return; }
    setConverting(true);
    try {
      const { data } = await api.post(`/leads/${id}/convert`, { deal_value: Number(dealValue) });
      toast.success("Converted to opportunity");
      navigate(`/deals/${data.id}`);
    } catch (e) { toast.error(apiError(e)); } finally { setConverting(false); }
  };

  if (isLoading) return <div className="flex justify-center py-20"><Loader2 className="h-6 w-6 animate-spin text-slate-400" /></div>;
  if (!lead) return <p className="text-slate-400">Lead not found.</p>;

  const canConvert = !lead.converted_opportunity_id;

  return (
    <div>
      <button onClick={() => navigate("/leads")} className="flex items-center gap-1.5 text-sm text-slate-500 hover:text-slate-800 mb-4" data-testid="back-btn">
        <ArrowLeft className="h-4 w-4" /> Back to Leads
      </button>

      <div className="flex flex-col sm:flex-row sm:items-start sm:justify-between gap-3 mb-6">
        <div>
          <div className="flex items-center gap-2 flex-wrap">
            <h1 className="font-heading text-2xl font-bold text-slate-900" data-testid="lead-title">{lead.project_name || lead.name}</h1>
            <StatusBadge kind="lead" value={lead.lead_status} />
            {lead.rating !== "None" && <StatusBadge kind="rating" value={lead.rating} />}
            {lead.sla_state && lead.sla_state !== "n/a" && <StatusBadge kind="sla" value={lead.sla_state} testid="lead-sla-badge" />}
            <CompletenessBadge completeness={lead.completeness} />
            <DivisionTag id={lead.division_id} />
          </div>
          <p className="text-slate-500 mt-1">{lead.company}{lead.name && lead.name !== lead.project_name && lead.name !== lead.company ? ` · ${lead.name}` : ""}</p>
        </div>
        <div className="flex gap-2 flex-wrap items-center">
          <div className="flex items-center gap-1.5">
            <span className="text-xs text-slate-400 hidden sm:inline">Status</span>
            <StageInfo kind="lead" />
            <LeadStatusSelect leadId={lead.id} value={lead.lead_status} lostReason={lead.lost_reason} lostComments={lead.lost_comments}
              wonReason={lead.won_reason} wonComments={lead.won_comments}
              className="w-[150px]" onChanged={() => qc.invalidateQueries({ queryKey: ["lead", id] })} />
          </div>
          <Button variant="outline" onClick={() => setEditOpen(true)} className="gap-1.5" data-testid="edit-lead-btn"><Pencil className="h-4 w-4" /> Edit</Button>
          <AssignOwnerSelect entityType="lead" entityId={lead.id} ownerId={lead.owner_id}
            onAssigned={(res) => {
              const isManager = ["super_admin", "sales_head", "region_head", "sales_manager"].includes(user?.role);
              if (!isManager && res.owner_id !== user?.id) navigate("/leads");
              else qc.invalidateQueries({ queryKey: ["lead", id] });
            }} />
          <VoiceNoteButton entityType="lead" entityId={lead.id} company={lead.company} onSaved={() => qc.invalidateQueries({ queryKey: ["lead", id] })} />
          {user?.role !== "dealer" && !lead.converted_opportunity_id && (
            <AssignDealerButton lead={lead} userRole={user?.role} onAssigned={() => qc.invalidateQueries({ queryKey: ["lead", id] })} />
          )}
          <Button variant="outline" onClick={() => setSampleOpen(true)} className="gap-1.5" data-testid="request-sample-btn"><Package2 className="h-4 w-4" /> Request Sample</Button>
          {canConvert && (
            <span className="inline-flex items-center gap-1.5">
              <Button onClick={() => { setDealValue(lead.expected_value ? String(lead.expected_value) : ""); setConvertOpen(true); }}
                className="bg-slate-900 hover:bg-slate-800 gap-1.5" data-testid="convert-lead-btn"><GitBranch className="h-4 w-4" /> Convert to Opportunity</Button>
              <InfoHint testid="convert-policy-info" text="Only the deal value is needed to convert. Per sales policy, a lead becomes an Opportunity once a quotation has been shared with the client." />
            </span>
          )}
          {!["factory", "management"].includes(user?.role) && (
            <Button variant="outline" onClick={() => navigate(`/quotes/new?lead=${id}`)} className="gap-1.5" data-testid="lead-create-quote-btn">
              <FileText className="h-4 w-4" /> Create Quotation
            </Button>
          )}
          {lead.converted_opportunity_id && <Button variant="outline" onClick={() => navigate(`/deals/${lead.converted_opportunity_id}`)} className="gap-1.5" data-testid="view-deal-btn"><Send className="h-4 w-4" /> View Opportunity</Button>}
        </div>
      </div>

      <div className="grid lg:grid-cols-3 gap-6">
        <div className="lg:col-span-2 space-y-6">
          {!lead.universe_id && !lead.converted_opportunity_id && !["factory", "management", "dealer", "marketing"].includes(user?.role) && (
            <UniverseLinkBanner entityType="lead" record={lead} onLinked={() => qc.invalidateQueries({ queryKey: ["lead", id] })} />
          )}
          {lead.needs_details && !["factory", "management", "dealer", "marketing"].includes(user?.role) && (
            <Card className="p-4 border-amber-200 bg-amber-50/70 flex flex-wrap items-center gap-3" data-testid="lead-needs-details-banner">
              <AlertTriangle className="h-5 w-5 text-amber-600 shrink-0" />
              <div className="flex-1 min-w-[220px]">
                <p className="text-sm font-semibold text-amber-900">Exhibition lead{lead.event_name ? ` from ${lead.event_name}` : ""} — details still needed</p>
                <p className="text-xs text-amber-800">Add the expected <b>Value</b>, <b>EDC</b>, <b>Zone</b> and <b>Branch</b> so it counts properly in Sales BI. The flag clears automatically once they're filled.</p>
              </div>
              <Button onClick={() => setEditOpen(true)} className="bg-amber-600 hover:bg-amber-700 gap-1.5" data-testid="lead-complete-details-btn"><Pencil className="h-4 w-4" /> Complete details</Button>
            </Card>
          )}
          {lead.completeness && !lead.completeness.complete && (
            <CompletenessCard completeness={lead.completeness} entityLabel="lead" onComplete={() => setEditOpen(true)} />
          )}
          <Card className="p-5">
            <h3 className="font-heading font-semibold text-sm text-slate-800 mb-4">Lead Details</h3>
            <div className="grid grid-cols-2 sm:grid-cols-3 gap-4">
              <Detail label="Zone" value={lead.zone} />
              <Detail label="Branch" value={lead.branch} />
              <Detail label="UniVicoustic Specified" value={lead.univic_specified} />
              <Detail label="Value" value={lead.expected_value ? formatINRShort(lead.expected_value) : null} />
              <Detail label="EDC" value={lead.edc_date ? formatDate(lead.edc_date) : null} />
              <Detail label="Universe Company" value={lead.universe_company} />
              <Detail label="Dealer Involved" value={lead.dealer_involved} />
              <Detail label="Identified" value={lead.identified} />
              <Detail label="Architect / Designer" value={lead.architect_universe_id ? <button className="text-brand hover:underline" onClick={() => navigate(`/universe?q=${encodeURIComponent(lead.architect_universe_company || lead.architect)}`)} data-testid="lead-architect-universe-link">{lead.architect} · Universe</button> : lead.architect} />
              <Detail label="D&B / GC" value={lead.dnb_gc_universe_id ? <button className="text-brand hover:underline" onClick={() => navigate(`/universe?q=${encodeURIComponent(lead.dnb_gc_universe_company || lead.dnb_gc)}`)} data-testid="lead-dnb-universe-link">{lead.dnb_gc} · Universe</button> : lead.dnb_gc} />
              {lead.team_split?.length > 0 && (
                <div className="col-span-2" data-testid="lead-team-split">
                  <p className="text-[11px] font-semibold uppercase tracking-wider text-slate-400 mb-1">Team Split</p>
                  <div className="flex flex-wrap gap-1.5">
                    {lead.team_split.map((m) => (
                      <span key={m.user_id} className="text-[11px] px-2 py-1 rounded-full bg-indigo-50 text-indigo-700 border border-indigo-100 font-medium">
                        {m.name}{m.region ? ` (${m.region})` : ""} — {m.pct}%
                      </span>
                    ))}
                  </div>
                </div>
              )}
              <Detail label="End Client — Decision Maker" value={lead.end_client_name} />
              <Detail label="End Client — Contact" value={lead.end_client_phone} />
              <Detail label="End Client — Email" value={lead.end_client_email} />
              {(lead.email || lead.end_client_email) && (
                <div className="col-span-full"><EmailStatusBadge email={lead.email || lead.end_client_email} /></div>
              )}
              <Detail label="Competition" value={lead.competition} />
              <Detail label="Type of Product" value={lead.product_type} />
              <Detail label="Total Area (Lakh sqft)" value={lead.area_lacs_sqft} />
              <Detail label="Phone" value={lead.phone} />
              <Detail label="Email" value={lead.email} />
              <Detail label="Designation" value={lead.designation} />
              <Detail label="Owner" value={lead.owner_name} />
              <Detail label="Dealer" value={lead.dealer_name} />
              <Detail label="Account Category" value={lead.account_category} />
              <Detail label="Inquiry Type" value={lead.inquiry_type} />
              <Detail label="Lead Source" value={lead.lead_source} />
              <Detail label="Event / Exhibition" value={lead.event_name} />
              {lead.showroom_interest && <Detail label="Showroom visit" value={`${{ yes: "Yes", later: "Perhaps later", no: "No" }[lead.showroom_interest] || lead.showroom_interest}${lead.showroom_locations?.length ? ` · ${lead.showroom_locations.join(", ")}` : ""}`} />}
              {lead.stay_connected?.length > 0 && <Detail label="Stay connected" value={lead.stay_connected.map((k) => ({ showroom_news: "Showroom news & invitations", new_products: "New products & collections" }[k] || k)).join(", ")} />}
              {lead.card_scan_id && <div className="col-span-2"><CardImage scanId={lead.card_scan_id} /></div>}
              <Detail label="Region" value={lead.region} />
              <Detail label="Location" value={lead.location} />
              <Detail label="Industry" value={lead.industry} />
              <Detail label="Employees" value={lead.no_of_employees} />
              <Detail label="Annual Revenue" value={lead.annual_revenue ? formatINR(lead.annual_revenue) : null} />
              {lead.won_reason && lead.lead_status === "Qualified" && <Detail label="Won Reason" value={`${lead.won_reason}${lead.won_comments ? ` — ${lead.won_comments}` : ""}`} />}
              {lead.lost_reason && <Detail label="Lost Reason" value={`${lead.lost_reason}${lead.lost_comments ? ` — ${lead.lost_comments}` : ""}`} />}
              {lead.registered_dealer_name && <Detail label="Registered Dealer" value={lead.registered_dealer_name} />}
              {lead.registration_expires_at && <Detail label="Registration Until" value={formatDate(lead.registration_expires_at)} />}
              {lead.sla_due_at && <Detail label="First-Response SLA" value={formatDate(lead.sla_due_at)} />}
              <Detail label="Created" value={formatDate(lead.created_at)} />
            </div>
            {(lead.street || lead.city) && (
              <div className="mt-4 pt-4 border-t">
                <Detail label="Address" value={[lead.street, lead.city, lead.state, lead.zip].filter(Boolean).join(", ")} />
              </div>
            )}
            {lead.description && <div className="mt-4 pt-4 border-t"><Detail label="Description" value={lead.description} /></div>}
            {lead.remarks && <div className="mt-3"><Detail label="Remarks" value={lead.remarks} /></div>}
          </Card>

          {lead.large_deal_details && <LargeDealCard d={lead.large_deal_details} value={lead.expected_value} edc={lead.edc_date} />}

          <Card className="p-5" data-testid="lead-quotations-card">
            <div className="flex items-center justify-between mb-4">
              <h3 className="font-heading font-semibold text-sm text-slate-800">Quotations</h3>
              {!["factory", "management"].includes(user?.role) && (
                <Button size="sm" className="bg-slate-900 hover:bg-slate-800 gap-1.5" onClick={() => navigate(`/quotes/new?lead=${id}`)} data-testid="lead-quote-card-create-btn">
                  <FileText className="h-4 w-4" /> Create Quotation
                </Button>
              )}
            </div>
            {(lead.quotations || []).length === 0 ? (
              <p className="text-sm text-slate-400 py-3 text-center">No quotations yet. Once a quote is shared, this lead becomes an Opportunity.</p>
            ) : (
              <div className="space-y-2">
                {lead.quotations.map((q) => (
                  <button key={q.id} onClick={() => navigate(`/quotes/${q.id}`)} className="w-full flex items-center justify-between p-3 rounded-md border border-slate-200 hover:border-slate-300 hover:bg-slate-50 transition-colors text-left" data-testid={`lead-quote-link-${q.id}`}>
                    <div className="flex items-center gap-2">
                      <FileText className="h-4 w-4 text-slate-400" />
                      <span className="text-sm font-medium">{q.quote_number}</span>
                      <span className="text-xs text-slate-400">v{q.version_number}</span>
                    </div>
                    <div className="flex items-center gap-3">
                      <span className="font-mono text-sm">{formatINR(q.total_amount)}</span>
                      <StatusBadge kind="quote" value={q.status} />
                    </div>
                  </button>
                ))}
              </div>
            )}
          </Card>

          <EventsCard entityType="lead" entityId={lead.id} company={lead.company} canEdit={user?.role !== "factory" && user?.role !== "management"} onSaved={() => qc.invalidateQueries({ queryKey: ["lead", id] })} />

          <CallHistoryCard linkedType="lead" linkedId={lead.id} company={lead.company} />
        </div>

        <div className="space-y-4">
          {user?.role !== "factory" && user?.role !== "management" && <RemindersCard entityType="lead" entityId={lead.id} />}
          <Card className="p-5">
            <h3 className="font-heading font-semibold text-sm text-slate-800 mb-3">Add Note / Activity</h3>
            <Textarea value={note} onChange={(e) => setNote(e.target.value)} placeholder="Log a call, meeting or note…" rows={3} data-testid="note-input" />
            <Button onClick={addNote} size="sm" className="mt-2 bg-slate-900 hover:bg-slate-800 w-full" data-testid="add-note-btn">Add to timeline</Button>
          </Card>
          <Card className="p-5">
            <h3 className="font-heading font-semibold text-sm text-slate-800 mb-4">Activity Timeline</h3>
            <ActivityTimeline activities={lead.activities} />
          </Card>
        </div>
      </div>

      <LeadFormDialog open={editOpen} onOpenChange={setEditOpen} initial={lead} onSaved={() => qc.invalidateQueries({ queryKey: ["lead", id] })} />
      <Dialog open={convertOpen} onOpenChange={setConvertOpen}>
        <DialogContent className="sm:max-w-sm">
          <DialogHeader><DialogTitle>Convert to Opportunity</DialogTitle></DialogHeader>
          <div className="space-y-1.5">
            <Label className="text-xs font-medium text-slate-700">Deal Value (INR) *</Label>
            <Input type="number" min="1" value={dealValue} onChange={(e) => setDealValue(e.target.value)}
              placeholder="e.g. 2500000" autoFocus data-testid="convert-deal-value" />
            <p className="text-xs text-slate-400">Only the deal value is needed — everything else carries over from the lead and stays editable on the opportunity.</p>
          </div>
          <Button onClick={convert} disabled={converting || !Number(dealValue)} className="bg-slate-900 hover:bg-slate-800 gap-1.5 w-full" data-testid="convert-confirm-btn">
            {converting ? <Loader2 className="h-4 w-4 animate-spin" /> : <GitBranch className="h-4 w-4" />} Convert
          </Button>
        </DialogContent>
      </Dialog>
      <SampleRequestDialog open={sampleOpen} onOpenChange={setSampleOpen}
        context={{ lead_id: lead.id, company: lead.company, contact_name: lead.name }}
        onSaved={() => qc.invalidateQueries({ queryKey: ["lead", id] })} />
    </div>
  );
}
